import { useMemo } from 'react'
import { useParams } from 'react-router'
import { useMutation, useQuery } from '@tanstack/react-query'

import { SpatialPolicy, SpatialPolicyContext } from '@/models/spatialPolicy'
import { SpatialPolicyDocument } from '@/models/spatialPolicyTypes'
import { MDSOperatorResponse } from '@/modules/api/openapi/models/MDSOperatorResponse'
import { PolicyCategoryEnum } from '@/modules/api/openapi/models/PolicyCategoryEnum'
import { RegionResponse } from '@/modules/api/openapi/models/RegionResponse'
import { invalidateQueryCachePredicate } from '@/modules/api/queryClient'
import { internalApi } from '@/modules/api/request'
import { useAllLayers } from '@/modules/layers/queries'
import { useMdsOperators } from '@/modules/operator/hooks'
import { POLICIES_COMPLIANCE_SUMMARY_ENDPOINT } from '@/modules/policiesV1/compliance/utils'
import {
  COMPLIANCE_FOR_POLICY_ENDPOINT,
  LIST_POLICIES_ENDPOINT,
} from '@/modules/policiesV1/complianceDetails/utils'
import { PoliciesService } from '@/modules/policiesV1/policyLibrary/service'
import { useCurrentRegion } from '@/modules/urlRouting/hooks'

const policiesService = new PoliciesService()

export const useAllowedPolicyOperators = (policy?: SpatialPolicy): MDSOperatorResponse[] => {
  const { data } = useMdsOperators()

  if (!policy || !data?.items) return []

  return policy.operators
    ? data.items.filter(({ slug }) => policy.operators?.includes(slug))
    : data.items
}

/**
 * The region/layer context `SpatialPolicy` needs for its date + geography getters, built from
 * React Query data (replaces the model's old `appUIStore`/`layerStore` reads — migration §2.3).
 */
export const usePolicyContext = (): SpatialPolicyContext => {
  const {
    data: { timezone },
  } = useCurrentRegion<RegionResponse>()
  // Load every layer for the UUID->name lookup. A single page (the default 50) silently
  // truncates regions with many layers, so policies whose geography falls past the first page
  // render as "Citywide"/blank.
  const { data: layers } = useAllLayers()

  return useMemo(() => {
    const nameByUuid = new Map((layers?.items ?? []).map(l => [l.shapeLayerUuid, l.layerName]))
    return {
      timezone,
      getLayerName: (uuid?: string | null) => (uuid ? nameByUuid.get(uuid) : undefined),
    }
  }, [timezone, layers])
}

export const useMobilityPolicyFromPath = () => {
  const { policyId } = useParams()
  const { data, isLoading, isError } = useMobilityPolicies()
  const context = usePolicyContext()
  return useMemo(() => {
    const policyDoc = data?.data.find(policy => policy.constant_policy_uuid === policyId)
    const policy = policyDoc
      ? new SpatialPolicy(policyDoc as SpatialPolicyDocument, context)
      : undefined
    return { policy, isLoading, isError }
  }, [policyId, data, isLoading, isError, context])
}

export const useMobilityPolicies = () => {
  const {
    data: { regionId },
  } = useCurrentRegion()

  return useQuery({
    queryKey: [`/regions/${regionId}/policies/list/mobility`],
    queryFn: async () =>
      await internalApi.policiesV1.listPolicies({
        requestBody: { region_id: regionId, policy_category: PolicyCategoryEnum.MOBILITY },
      }),
  })
}

/** The mobility policy list as `SpatialPolicy` models (replaces `PoliciesStore.policies`). */
export const usePolicies = (): SpatialPolicy[] => {
  const { data } = useMobilityPolicies()
  const context = usePolicyContext()
  return useMemo(
    () => data?.data.map(doc => new SpatialPolicy(doc as SpatialPolicyDocument, context)) ?? [],
    [data, context]
  )
}

/** Deletes a policy and invalidates the list query so it refetches. */
export const useDeleteMobilityPolicy = () =>
  useMutation({
    mutationFn: (policyUUID: string) => policiesService.delete(policyUUID),
    onSuccess: () => invalidateQueryCachePredicate(LIST_POLICIES_ENDPOINT),
  })

/** Publishes/unpublishes a policy and invalidates the list query so it refetches. */
export const usePublishMobilityPolicy = () =>
  useMutation({
    mutationFn: ({ policyUUID, published }: { policyUUID: string; published: boolean }) =>
      policiesService.publish(policyUUID, published),
    onSuccess: () => invalidateQueryCachePredicate(LIST_POLICIES_ENDPOINT),
  })

/**
 * Saves (create or update) a policy and invalidates every cache that depends on policies
 * (the list plus both compliance views) so they refetch.
 */
export const useSaveMobilityPolicy = () =>
  useMutation({
    mutationFn: (policyJson: object) => policiesService.save(policyJson),
    onSuccess: () => {
      invalidateQueryCachePredicate(LIST_POLICIES_ENDPOINT)
      invalidateQueryCachePredicate(POLICIES_COMPLIANCE_SUMMARY_ENDPOINT)
      invalidateQueryCachePredicate(COMPLIANCE_FOR_POLICY_ENDPOINT)
    },
  })
